import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import type { components } from "@/api/types";
import { useDemoPosition } from "@/entities/demo";
import { RobotAvatar } from "@/entities/robot";
import { StatusChip } from "@/entities/user-robot";
import { useStopDemoPosition } from "@/features/demo-actions";
import { ApiError, classify, useMode } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { formatMoney, formatPercent, moneySign } from "@/shared/lib";
import { Button, Modal, useToast } from "@/shared/ui";
import { EquityChart } from "@/widgets/equity-chart";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { DailyResultRow } from "@/entities/user-robot";

type ChartPeriod = components["schemas"]["ChartPeriod"];

/**
 * Деталь демо-позиции (доработки §3.1: «остановить демостратегию»).
 *
 * Список дневных результатов не виртуализируется, в отличие от реального
 * экрана: демо-позиция живёт не дольше `demo_position_ttl_days` (по умолчанию
 * месяц), то есть строк здесь десятки, а не сотни. Виртуализатор ради тридцати
 * строк — сложность без выгоды.
 *
 * Пополнения нет: в демо другая сумма — это остановка и новый запуск. Так же
 * устроен и бэкенд, поэтому кнопки «доложить» здесь просто нет.
 */
export function DemoPositionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { setMode } = useMode();
  const { id } = useParams();
  const positionId = Number(id);

  const [confirming, setConfirming] = useState(false);
  const [period, setPeriod] = useState<ChartPeriod>("90d");

  const { data, isPending, isError, isFetching, error, refetch } = useDemoPosition(
    positionId,
    period,
  );
  const stop = useStopDemoPosition(positionId);

  if (!Number.isFinite(positionId)) {
    return (
      <ScreenState
        tone="info"
        icon="questionMark"
        title={t("myRobots.notFound.title")}
        body={t("myRobots.notFound.body")}
      />
    );
  }

  if (isPending) return <ScreenSkeleton blocks={[112, 300, 240]} />;

  if (isError) {
    const notFound = error instanceof ApiError && error.code === "NOT_FOUND";
    return (
      <ScreenState
        tone={notFound ? "info" : "loss"}
        icon={notFound ? "questionMark" : "warningFill"}
        title={t(notFound ? "myRobots.notFound.title" : "states.error.title")}
        body={t(notFound ? "myRobots.notFound.body" : "states.error.body")}
        {...(notFound
          ? {}
          : { action: { label: t("states.error.action"), onClick: () => void refetch() } })}
      />
    );
  }

  const sign = moneySign(data.earned_total);
  const tone =
    sign < 0 ? "text-status-loss" : sign > 0 ? "text-status-profit" : "text-text-secondary";
  const active = data.status === "active";

  const confirmStop = () => {
    stop.mutate(undefined, {
      onSuccess: () => {
        setConfirming(false);
        toast.show({ tone: "loss", title: t("demo.stopped", { name: data.robot.name }) });
      },
      onError: (stopError) => {
        setConfirming(false);
        const reaction = classify(stopError);
        // Позиция уже остановлена другой вкладкой — данные протухли, не ошибка.
        if (reaction.kind === "conflict") void refetch();
        toast.show({
          tone: "loss",
          title: reaction.kind === "conflict" ? reaction.message : t("states.error.title"),
        });
      },
    });
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <header className="flex h-[42px] items-center gap-2">
        <button
          type="button"
          onClick={() => void navigate(-1)}
          data-haptic="nav"
          aria-label={t("common.back")}
          className="press text-h1 font-medium text-text-primary"
        >
          ‹
        </button>
        <h1 className="text-h2 font-semibold text-text-primary">{data.robot.name}</h1>
      </header>

      <section className="flex flex-col gap-3 rounded-[20px] border border-border-subtle bg-background-elevated p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-[9px]">
            <RobotAvatar robot={data.robot} size={28} className="mt-0.5" />
            <div className="flex flex-col gap-0.5">
              <span className="text-body font-semibold text-text-primary">
                {data.robot.name}
              </span>
              <span className="text-caption font-normal text-text-secondary">
                {t("demo.invested")} {formatMoney(data.amount, { suffix: "USDT" })}
              </span>
            </div>
          </div>
          <StatusChip status={data.status} />
        </div>

        <dl className="flex items-end justify-between">
          <div className="flex flex-col gap-1">
            <dt className="text-caption font-medium text-text-secondary">
              {t("demo.earned")}
            </dt>
            <dd className={`text-h3 font-semibold ${tone}`}>
              {formatMoney(data.earned_total, { sign: "always", suffix: "USDT" })}
            </dd>
          </div>
          <dd className={`text-label font-semibold ${tone}`}>
            {formatPercent(data.earned_pct, { sign: "always" })}
          </dd>
        </dl>

        {/* Ожидающий подключения капитал: правило «запущенный сегодня
            начисляется с завтра» действует и в демо, и молчать о нём нельзя —
            иначе первый день выглядит как сломанный робот. */}
        {moneySign(data.capital_pending) > 0 ? (
          <div className="flex items-baseline justify-between text-caption">
            <span className="font-normal text-text-secondary">{t("demo.pending")}</span>
            <span className="font-semibold text-status-warning">
              {formatMoney(data.capital_pending, { suffix: "USDT" })}
            </span>
          </div>
        ) : null}

        {moneySign(data.fee_total) > 0 ? (
          <div className="flex items-baseline justify-between text-caption">
            <span className="font-normal text-text-secondary">{t("myRobots.feeTotal")}</span>
            <span className="font-semibold text-text-primary">
              {formatMoney(data.fee_total, { suffix: "USDT" })}
            </span>
          </div>
        ) : null}
      </section>

      <EquityChart
        points={data.chart}
        total={data.earned_total}
        totalPct={data.earned_pct}
        period={data.chart_period}
        onPeriodChange={setPeriod}
        busy={isFetching}
      />

      <section className="flex flex-col gap-2 rounded-[20px] border border-border-subtle bg-background-elevated p-4">
        <h2 className="text-body font-semibold text-text-primary">{t("myRobots.history")}</h2>
        {data.daily_results.length === 0 ? (
          <p className="py-6 text-center text-caption font-normal text-text-secondary">
            {t("myRobots.historyEmpty")}
          </p>
        ) : (
          <div className="no-scrollbar max-h-[360px] overflow-y-auto">
            {data.daily_results.map((item) => (
              <DailyResultRow key={item.date} item={item} />
            ))}
          </div>
        )}
      </section>

      {/* CTA §3.1: «Запустить на реальном счёте» → переход в реальный режим БЕЗ
          автоматического списания средств. Поэтому кнопка ведёт на экран
          суммы, а не запускает робота: подтверждение и списание остаются за
          обычным реальным флоу, со всеми его проверками. */}
      <Button
        size="cta"
        block
        onClick={() => {
          setMode("real");
          void navigate(ROUTES.robotBuy(data.robot.id));
        }}
      >
        {t("demo.toRealRobot")}
      </Button>

      {active ? (
        <Button size="cta" variant="danger" block onClick={() => setConfirming(true)}>
          {t("demo.stop")}
        </Button>
      ) : null}

      {confirming ? (
        <Modal
          tone="danger"
          icon="handPalm"
          title={t("demo.stopConfirm.title")}
          body={t("demo.stopConfirm.body", { name: data.robot.name })}
          onClose={() => setConfirming(false)}
          footer={
            <div className="flex flex-col gap-2">
              <Button
                size="cta"
                variant="danger"
                block
                loading={stop.isPending}
                onClick={confirmStop}
              >
                {t("demo.stopConfirm.action")}
              </Button>
              <Button size="cta" variant="ghost" block onClick={() => setConfirming(false)}>
                {t("common.cancel")}
              </Button>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
